import { useEffect, useState, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 2000;

const isFinalFailure = (paymentStatus) =>
  paymentStatus === "failed" || paymentStatus === "expired";

async function fetchSessionStatus(sessionId) {
  const r = await axios.get(`${API}/payments/status/${sessionId}`);
  return r.data;
}

export function PaymentSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const sessionId = new URLSearchParams(loc.search).get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const [kind, setKind] = useState("booking");

  const poll = useCallback(
    async (n, cancelledRef) => {
      if (cancelledRef.current) return;
      try {
        const data = await fetchSessionStatus(sessionId);
        setKind(data.kind || "booking");
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (isFinalFailure(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (err) {
        // Non-fatal: keep polling until MAX_POLL_ATTEMPTS
        // eslint-disable-next-line no-console
        console.warn("[PaymentSuccess] status poll failed:", err?.message || err);
      }
      if (n >= MAX_POLL_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1, cancelledRef), POLL_INTERVAL_MS);
    },
    [sessionId]
  );

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const cancelledRef = { current: false };
    poll(0, cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [sessionId, poll]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-[color:var(--primary)] mx-auto animate-spin" />
          <h1 className="font-display text-4xl mt-6">Confirming your payment…</h1>
          <p className="text-neutral-500 mt-2">Attempt {attempts + 1} / {MAX_POLL_ATTEMPTS}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full grid place-items-center mx-auto" style={{ background: "var(--primary)" }}>
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-5xl mt-6">You're all set.</h1>
          <p className="text-neutral-600 mt-3 max-w-md mx-auto leading-relaxed">
            {kind === "membership"
              ? "Priority Membership is active. WhatsApp us anytime."
              : "Payment received. A verified Mate is being matched — expect a match under 10 minutes."}
          </p>
          <Link to="/dashboard" data-testid="success-dashboard-btn" className="btn-primary inline-block mt-8">Go to dashboard</Link>
        </>
      )}
      {(status === "failed" || status === "timeout" || status === "error") && (
        <>
          <XCircle className="w-14 h-14 text-red-500 mx-auto" />
          <h1 className="font-display text-4xl mt-6">Payment couldn't be confirmed</h1>
          <p className="text-neutral-500 mt-2">Please try again or contact support.</p>
          <button className="btn-primary mt-6" onClick={() => nav("/services")}>Try again</button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center">
      <XCircle className="w-14 h-14 text-neutral-400 mx-auto" />
      <h1 className="font-display text-4xl mt-6">Payment cancelled</h1>
      <p className="text-neutral-500 mt-2">No charge was made.</p>
      <Link to="/services" data-testid="cancel-back-btn" className="btn-primary inline-block mt-6">Back to services</Link>
    </div>
  );
}
